/* eslint-disable react/no-unknown-property -- react-three/fiber uses JSX props ESLint doesn't recognise */
/**
 * PitDesignPanel — Sprint D · Feb 2026.
 *
 * A Leapfrog/Datamine-style 3-D pit design surface:
 *   • six engineering inputs (bench height / batter angle / berm width /
 *     ramp width / ramp azimuth / radius) driven by sliders
 *   • live 3-D preview of the resulting benched shell (translucent
 *     shaded surface + animated bench-top polylines fading in
 *     top → bottom)
 *   • real-time tonnage / waste / strip-ratio readout when reconciled
 *     against a selected block model
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Loader2, Compass, Layers, MoveDown, Route } from "lucide-react";
import { formatTonnage } from "@/components/ssot/OreFigures";

const DEFAULT = {
  radius_top: 200,
  depth: 120,
  bench_height: 12,
  batter_angle_deg: 65,
  berm_width: 4,
  ramp_width: 20,
  ramp_azimuth_deg: 90,
};

function PitMesh({ vertices, triangles }) {
  const geometry = useMemo(() => {
    if (!vertices?.length || !triangles?.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertices.flat()), 3));
    g.setIndex(new THREE.BufferAttribute(new Uint32Array(triangles.flat()), 1));
    g.computeVertexNormals();
    return g;
  }, [vertices, triangles]);
  if (!geometry) return null;
  return (
    <group>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color="#f59e0b" transparent opacity={0.28}
          side={THREE.DoubleSide} depthWrite={false}
        />
      </mesh>
      <lineSegments>
        <wireframeGeometry attach="geometry" args={[geometry]} />
        <lineBasicMaterial color="#b45309" linewidth={1} />
      </lineSegments>
    </group>
  );
}

/**
 * Animate each bench-line polyline in from top → bottom over 0.8 s. Uses
 * a per-line `dashOffset` on a LineDashedMaterial so the effect reads
 * clearly at any viewport size.
 */
function BenchLines({ lines }) {
  const t0 = useRef(performance.now());
  const refs = useRef([]);

  useEffect(() => { t0.current = performance.now(); refs.current = []; }, [lines]);

  useFrame(() => {
    const elapsed = (performance.now() - t0.current) / 1000.0;
    refs.current.forEach((mat, i) => {
      if (!mat) return;
      const perLine = 0.9;
      const start = i * 0.08;
      const t = Math.max(0, Math.min(1, (elapsed - start) / perLine));
      mat.opacity = t;
      mat.needsUpdate = true;
    });
  });

  return (
    <group>
      {lines.map((polyline, i) => {
        if (!polyline?.length) return null;
        // Close the loop so the ring is visually continuous.
        const closed = [...polyline, polyline[0]];
        const pts = closed.map((p) => new THREE.Vector3(p[0], p[1], p[2]));
        const geometry = new THREE.BufferGeometry().setFromPoints(pts);
        return (
          <line key={i}>
            <primitive object={geometry} attach="geometry" />
            <lineBasicMaterial
              ref={(m) => (refs.current[i] = m)}
              color="#facc15"
              transparent
              opacity={0}
              linewidth={2}
            />
          </line>
        );
      })}
    </group>
  );
}

export default function PitDesignPanel({
  projectId, blockModelId = null, onChange, className = "",
}) {
  const [params, setParams] = useState(DEFAULT);
  const [busy, setBusy]     = useState(false);
  const [preview, setPreview] = useState(null);
  const _timer = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    if (_timer.current) clearTimeout(_timer.current);
    _timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post(
          `/projects/${projectId}/pit-design/preview`,
          { center: [0, 0, 100], ...params, block_model_id: blockModelId || null },
        );
        setPreview(data);
        onChange?.({ params, result: data });
      } catch (_e) { setPreview(null); }
      finally { setBusy(false); }
    }, 500);
    return () => _timer.current && clearTimeout(_timer.current);
  }, [projectId, blockModelId, params, onChange]);

  const update = (k, v) => setParams((p) => ({ ...p, [k]: Number(v) }));

  const row = (key, label, min, max, step, unit, icon) => (
    <div className="space-y-1.5" data-testid={`pit-${key}-block`}>
      <div className="flex items-center justify-between text-[11px] font-mono">
        <span className="flex items-center gap-1.5 text-neutral-700">
          {icon}<span>{label}</span>
        </span>
        <span className="text-neutral-900 tabular-nums">
          {Number(params[key]).toFixed(step < 1 ? 1 : 0)} {unit}
        </span>
      </div>
      <Slider
        value={[params[key]]} min={min} max={max} step={step}
        onValueChange={(v) => update(key, v[0])}
        data-testid={`pit-${key}-slider`}
      />
    </div>
  );

  const recon = preview?.reconciliation;

  return (
    <div className={`border border-border bg-card ${className}`} data-testid="pit-design-panel">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MoveDown className="h-4 w-4 text-amber-500" strokeWidth={1.75} />
          <span className="font-heading font-bold text-sm">3D Pit Design</span>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
        </div>
        <span className="text-[10px] font-mono text-neutral-500">
          {preview ? `${preview.benches} benches · final ⌀ ${(preview.final_radius * 2).toFixed(0)} m` : "—"}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 p-4">
        <div className="space-y-3">
          {row("radius_top",       "Radius (top)",   50, 800, 5,  "m", <Compass className="h-3 w-3" />)}
          {row("depth",            "Pit depth",      20, 500, 5,  "m", <MoveDown className="h-3 w-3" />)}
          {row("bench_height",     "Bench height",    3,  30, 1,  "m", <Layers  className="h-3 w-3" />)}
          {row("batter_angle_deg", "Batter angle",   30,  85, 1,  "°", <Layers  className="h-3 w-3" />)}
          {row("berm_width",       "Berm width",      0,  15, 0.5, "m", <Layers className="h-3 w-3" />)}
          {row("ramp_width",       "Ramp width",      0,  40, 1,  "m", <Route className="h-3 w-3" />)}
          {row("ramp_azimuth_deg", "Ramp azimuth",    0, 360, 5,  "°", <Compass className="h-3 w-3" />)}

          {recon && (
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border"
                 data-testid="pit-reconciliation">
              <div className="border border-neutral-200 p-2">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">Ore inside pit</div>
                <div className="text-sm font-heading font-black text-neutral-900 tabular-nums"
                     data-testid="pit-ore-value">
                  {formatTonnage(recon.ore_tonnage || 0, "auto", { decimals: 2 })}
                </div>
              </div>
              <div className="border border-neutral-200 p-2">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">Waste inside pit</div>
                <div className="text-sm font-heading font-black text-neutral-900 tabular-nums"
                     data-testid="pit-waste-value">
                  {formatTonnage(recon.waste_tonnage || 0, "auto", { decimals: 2 })}
                </div>
              </div>
              <div className="border border-neutral-200 p-2 col-span-2">
                <div className="flex items-center justify-between">
                  <span>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">Strip ratio</div>
                    <div className="text-sm font-heading font-black text-neutral-900 tabular-nums"
                         data-testid="pit-strip-value">
                      {Number(recon.strip_ratio || 0).toFixed(2)} : 1
                    </div>
                  </span>
                  <span className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">Total</div>
                    <div className="text-sm font-heading font-black text-neutral-900 tabular-nums"
                         data-testid="pit-total-value">
                      {formatTonnage(recon.total_tonnage || 0, "auto", { decimals: 2 })}
                    </div>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border border-neutral-200 bg-neutral-50 relative min-h-[320px]"
             data-testid="pit-canvas-wrap">
          <Canvas
            camera={{ position: [500, 500, 500], fov: 42 }}
            style={{ background: "#F8FAFC" }}
            data-testid="pit-3d-canvas"
          >
            <ambientLight intensity={0.7} />
            <directionalLight position={[200, 400, 200]} intensity={0.9} />
            <axesHelper args={[params.radius_top * 1.1]} />
            <gridHelper args={[params.radius_top * 3, 30, "#cbd5e1", "#e2e8f0"]}
                        rotation={[Math.PI / 2, 0, 0]} />
            {preview?.mesh && (
              <PitMesh vertices={preview.mesh.vertices} triangles={preview.mesh.triangles} />
            )}
            {preview?.bench_lines && (
              <BenchLines lines={preview.bench_lines} />
            )}
            <OrbitControls makeDefault />
          </Canvas>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between
                          text-[10px] font-mono text-neutral-600 pointer-events-none">
            <span>
              <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#f59e0b" }} />Pit shell&nbsp;
              <span className="inline-block w-2 h-2 rounded-full mx-1" style={{ background: "#facc15" }} />Bench lines
            </span>
            <span>{preview?.mesh?.triangles?.length?.toLocaleString?.() ?? 0} tri</span>
          </div>
        </div>
      </div>
    </div>
  );
}
